import { NOTIFICATION_PANEL_LIMIT } from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/api/module-client';

/**
 * Query keys for the notification centre.
 *
 * Built here rather than added to `src/lib/query-keys.ts` because that file is
 * shared and currently owned elsewhere; the wiring note carries the block to
 * fold in. The shape matches the factory's conventions exactly, so moving it
 * is a cut and paste and no call site changes.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (unreadOnly: boolean) => ['notifications', 'list', { unreadOnly }] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,
};

/**
 * The badge.
 *
 * Polled, because the reminder sweep writes rows from a job the browser knows
 * nothing about — without a poll the bell only updates when something else
 * happens to refetch. One minute matches the sweep's own default interval, so
 * a reminder shows up within roughly two minutes of being due without putting
 * a socket in the way. `refetchIntervalInBackground` is off: a backgrounded
 * tab polling forever is how a laptop battery disappears.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/** The panel's page. Only fetched while the panel is open. */
export function useNotifications(unreadOnly: boolean, enabled: boolean) {
  return useQuery({
    queryKey: notificationKeys.list(unreadOnly),
    queryFn: () => notificationsApi.list({ limit: NOTIFICATION_PANEL_LIMIT, offset: 0, unreadOnly }),
    enabled,
    staleTime: 15_000,
  });
}

/**
 * Marking read invalidates the list AND the count. Invalidating only the list
 * would leave a badge claiming three unread above a panel showing none, which
 * is the exact moment people stop trusting a notification centre.
 */
function useNotificationWrite<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkNotificationRead() {
  return useNotificationWrite((id: number) => notificationsApi.markRead(id));
}

export function useMarkAllNotificationsRead() {
  return useNotificationWrite(() => notificationsApi.markAllRead());
}
